/**
 * Thin fetch wrapper. Sets the Authorization header from the auth store and
 * raises a structured error on non-2xx so TanStack Query gets a meaningful
 * shape to display.
 */
import { API_BASE_URL } from './config.js';
import { useAuth } from './auth.js';

export type ApiError = {
  status: number;
  code: string;
  message: string;
  details?: unknown;
};

export class ApiFetchError extends Error implements ApiError {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.name = 'ApiFetchError';
  }
}

export type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Pass `true` to skip the bearer header even when the store has a token. */
  skipAuth?: boolean;
  /** Extra headers. */
  headers?: Record<string, string>;
  /** AbortSignal - TanStack Query forwards one. */
  signal?: AbortSignal;
};

export async function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(opts.headers ?? {}),
  };
  if (!opts.skipAuth) {
    const token = useAuth.getState().accessToken;
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'omit',
  });

  if (!res.ok) {
    let payload: { error?: { code?: string; message?: string; details?: unknown } } = {};
    try {
      payload = (await res.json()) as typeof payload;
    } catch {
      /* non-JSON error body - fall through */
    }
    const code = payload.error?.code ?? `http_${res.status}`;
    const message = payload.error?.message ?? `Request failed (${res.status}).`;
    throw new ApiFetchError(res.status, code, message, payload.error?.details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
